// src/api.js
import { saveCachedResponse, getCachedResponse, clearOfflineData } from './services/db';
import { isOnline } from './services/sync';

const BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

export const saveToken = (t) => localStorage.setItem("erp_token", t);
export const clearToken = () => { localStorage.removeItem("erp_token"); clearOfflineData().catch(() => {}); };
export const getToken = () => localStorage.getItem("erp_token") || "";
export const safeData = (data, defaultValue = []) => data && Array.isArray(data) ? data : defaultValue;

const cacheKey = path => path;
const offlineMutationError = () =>
  new Error("Offline: this action requires a live connection and was not queued.");

const req = async (method, path, body, retryCount = 0, operationId = null) => {
  if (!isOnline()) {
    if (method === "GET") {
      const cached = await getCachedResponse(cacheKey(path));
      if (cached !== null) return cached;
      throw new Error("Offline: this information has not been loaded on this device yet.");
    }
    throw offlineMutationError();
  }

  try {
    const res = await fetch(`${BASE}${path}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        "x-auth-token": getToken(),
        ...(operationId ? { "x-operation-id": operationId, "x-expense-operation-id": operationId } : {}),
        ...(path.startsWith("/api/admin/") && getAdminAccountToken()
          ? { Authorization: `Bearer ${getAdminAccountToken()}` }
          : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    const data = await res.json();
    if (!res.ok) {
      if (method === "GET" && res.status === 429 && retryCount < 3) {
        await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1)));
        return req(method, path, body, retryCount + 1, operationId);
      }
      const e = new Error(data.error || "Request failed"); e.status = res.status; e.data = data; throw e;
    }
    if (method === "GET" && path.startsWith("/api/")) await saveCachedResponse(cacheKey(path), data);
    return data;
  } catch (err) {
    const networkFailure = err instanceof TypeError || err.message?.toLowerCase().includes("fetch") || !navigator.onLine;
    if (networkFailure && method === "GET" && retryCount < 1 && navigator.onLine) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      return req(method, path, body, retryCount + 1, operationId);
    }
    if (networkFailure && method === "GET") {
      const cached = await getCachedResponse(cacheKey(path));
      if (cached !== null) return cached;
    }
    if (networkFailure && method !== "GET" && path.startsWith("/api/")) {
      // A request that reached fetch may have committed before its response was lost.
      // Never queue or replay it with a fresh operation ID: that can double-debit cash.
      const uncertain = new Error("Connection interrupted. This action may have been saved. Check the latest records before submitting again.");
      uncertain.code = "MUTATION_OUTCOME_UNKNOWN";
      throw uncertain;
    }
    throw err;
  }
};

export const requestApi = req;

const csvExport = async (path) => {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "x-auth-token": getToken() },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || "Export failed");
  }
  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") || "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match?.[1] || `wavelines-export-${new Date().toISOString().slice(0, 10)}.csv`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ============ AUTH & BUSINESS ============
export const getMe = () => req("GET", "/auth/me");
export const logout = () => req("POST", "/auth/logout");
export const isAdmin = () => req("GET", "/auth/is-admin");
// Google Identity Services needs the OAuth client ID in the browser. It is
// a public identifier (not the OAuth secret), so the server exposes the same
// configured value at runtime for the standalone Developer/Admin portals.
// This avoids their sign-in button silently disappearing when a frontend
// build was made without REACT_APP_GOOGLE_CLIENT_ID.
export const getGoogleSignInConfig = () => req("GET", "/auth/google-client-id");
// Self-service registration — replaces the old admin-approval-gated
// /request-access flow. Lands the new business on the free Trial plan
// immediately (see server.js POST /auth/register).
export const registerBusiness = (payload) => req("POST", "/auth/register", payload);
export const updateFeatures = (features) => req("PUT", "/api/business/features", { features });
export const updateLogo = (logoUrl) => req("PUT", "/api/business/logo", { logoUrl });
// Guided "change business model" flow — routes/business.js. Swaps
// businessType and re-applies its module preset in one step. Same
// hide-don't-delete behavior as updateFeatures above — nothing is ever
// destroyed, so switching back to a prior type restores everything.
export const getBusinessType = () => req("GET", "/api/business/business-type");
export const updateBusinessType = (businessType) => req("PUT", "/api/business/business-type", { businessType });
export const selectBusiness = (selectorToken, businessId) =>
  fetch(`${BASE}/auth/select-business`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-auth-token": selectorToken },
    body: JSON.stringify({ businessId }),
  }).then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || "Failed"); return d; }));

// Same-login Live ↔ Demo Workspace controls. Authority is derived server-side.
export const getWorkspaces = () => req("GET", "/api/workspaces");
export const getCurrentWorkspace = () => req("GET", "/api/workspaces/current");
export const createDemoWorkspace = payload => req("POST", "/api/workspaces/demo", payload);
export const switchWorkspace = businessId => req("POST", "/api/workspaces/switch", { businessId });
export const setDemoWorkspaceTier = tier => req("PUT", "/api/workspaces/demo/tier", { tier });
export const resetDemoWorkspace = () => req("POST", "/api/workspaces/demo/reset", {});
export const completeDemoTour = () => req("POST", "/api/workspaces/tour-complete", {});

// Registering a new business from the business-picker page (this email
// already has a role at one or more OTHER businesses) — uses the
// selector token directly rather than the generic req() helper, since a
// selector session is intentionally never saved as the main erp_token.
export const registerBusinessFromSelector = (selectorToken, payload) =>
  fetch(`${BASE}/auth/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-auth-token": selectorToken },
    body: JSON.stringify(payload),
  }).then(r => r.json().then(d => { if (!r.ok) throw new Error(d.error || "Failed"); return d; }));


  // ============ ORDERS ============
export const getOrders = (p) => req("GET", `/api/orders?${new URLSearchParams(p)}`);
export const getOrder = (id) => req("GET", `/api/orders/${id}`);
export const createOrder = (d) => req("POST", "/api/orders", d);
export const updateOrder = (id, d) => req("PUT", `/api/orders/${id}`, d);
export const updateOrderProductionStatus = (id, productionStatus) => req("PUT", `/api/orders/${id}/production-status`, { productionStatus });
// Cancel/refund (Tier 17) — separate actions on purpose, see
// routes/orders.js's own comments on each route for why.
export const cancelOrder = (id, reason) => req("PUT", `/api/orders/${id}/cancel`, { reason });
export const refundOrder = (id, amount, reason) => req("PUT", `/api/orders/${id}/refund`, { amount, reason });
// Salvage (Tier 17, Phase 4) — turn a cancelled order's still-usable
// materials into a sellable Finished Good instead of a total write-off.
// materialsUsed: [{ rawInventoryItemId, quantity }] — bounded by what
// was originally allocated to the order (see routes/orders.js).
export const salvageOrder = (id, d) => req("POST", `/api/orders/${id}/convert-to-finished-good`, d);
export const exportOrders = () => csvExport("/api/orders/export/csv");
export const bulkUpdateOrders = (orderIds, action, data) => req("POST", "/api/orders/bulk", { orderIds, action, data });
// WhatsApp links (Tier 20, Growth+ plan) — see routes/orders.js's
// GET /:id/whatsapp-links. paymentId is optional — pass it right after
// recording a payment to get a receiptLink back instead of just invoice.
export const getOrderWhatsappLinks = (id, paymentId) => req("GET", `/api/orders/${id}/whatsapp-links${paymentId ? `?paymentId=${paymentId}` : ""}`);

// Staff-to-staff WhatsApp (Tier 23, Enterprise only) — assign an order
// to a staff member, then notify them with one click. See
// routes/orders.js's PUT /:id/assign and GET /:id/staff-whatsapp-link.
export const assignOrder = (id, assignedTo) => req("PUT", `/api/orders/${id}/assign`, { assignedTo });
export const getStaffWhatsappLink = (id) => req("GET", `/api/orders/${id}/staff-whatsapp-link`);
export const getMyAssignedOrders = () => req("GET", "/api/orders/me/assigned");

// ============ PUBLIC ORDER TRACKING (no login, Tier 20) ============
// GET /api/track/:token is unauthenticated on the backend — req() still
// sends whatever token happens to be in localStorage (or none), but the
// route never reads it, so this works identically for a signed-in
// manager previewing a link and a client with no account at all.
export const getOrderTracking = (token) => req("GET", `/api/track/${token}`);

// ============ CUSTOMERS (CRM, Tier 13 — Growth+ only) ============
export const getCustomers = (p) => req("GET", `/api/customers?${new URLSearchParams(p)}`);
export const searchCustomers = (q) => req("GET", `/api/customers/search?q=${encodeURIComponent(q)}`);
export const getCustomer = (id) => req("GET", `/api/customers/${id}`);
export const getCustomerHistory = (id) => req("GET", `/api/customers/${id}/history`);
export const getCustomerAccount = (id) => req("GET", `/api/customers/${id}/account`);
export const createCustomer = (d) => req("POST", "/api/customers", d);
export const updateCustomer = (id, d) => req("PUT", `/api/customers/${id}`, d);
export const archiveCustomer = (id) => req("DELETE", `/api/customers/${id}`);
export const exportCustomers = () => csvExport("/api/customers/export/csv");
// Bulk WhatsApp (Tier 20, Growth+ plan) — see routes/customers.js.
export const getWhatsappOverview = () => req("GET", "/api/customers/whatsapp-overview");
export const getBulkWhatsappLinks = (customerIds, message) => req("POST", "/api/customers/bulk-whatsapp-links", { customerIds, message });

// ============ SHOPS (Tier 21 — multi-shop, Enterprise only) ============
export const getShops = () => req("GET", "/api/shops");
export const createShop = (d) => req("POST", "/api/shops", d);
export const updateShop = (id, d) => req("PUT", `/api/shops/${id}`, d);
export const deactivateShop = (id) => req("DELETE", `/api/shops/${id}`);
export const getStaffOverview = () => req("GET", "/api/shops/staff-overview");
export const relocateStaff = (email, shopId) => req("PUT", `/api/shops/staff/${encodeURIComponent(email)}/relocate`, { shopId });
export const getShopReport = () => req("GET", "/api/shops/report");
// Per-shop stock allocation (Tier 25, extended Tier 27) — see
// routes/shops.js's /inventory and /inventory/transfer. materialType
// is "finishedGoods" (default) or "rawMaterial" — two entirely
// separate allocation pools that are never mixed in one call, per the
// standing instruction that finished goods and raw materials are
// always two distinct lists a manager moves stock within.
export const getShopInventory = (materialType = "finishedGoods") =>
  req("GET", `/api/shops/inventory?materialType=${materialType}`);
export const transferShopStock = (materialType, productId, fromShopId, toShopId, quantity, operationId) =>
  req("POST", "/api/shops/inventory/transfer", { materialType, productId, fromShopId, toShopId, quantity }, 0, operationId || window.crypto?.randomUUID?.() || ("op-" + Date.now() + "-" + Math.random().toString(36).slice(2)));

// ============ PAYMENTS ============
export const getPayments = (p) => req("GET", `/api/payments?${new URLSearchParams(p)}`);
export const createPayment = (d, operationId) => req("POST", "/api/payments", d, 0, operationId || window.crypto?.randomUUID?.() || ("op-" + Date.now() + "-" + Math.random().toString(36).slice(2)));
export const updatePayment = (id, d) => req("PUT", `/api/payments/${id}`, d);
export const exportPayments = () => csvExport("/api/payments/export/csv");
export const getReconciliation = (from, to) => req("GET", `/api/payments/reconcile?from=${from}&to=${to}`);

// ============ EXPENSES ============
export const getExpenses = (p) => req("GET", `/api/expenses?${new URLSearchParams(p)}`);
export const createExpense = (d, operationId) => req("POST", "/api/expenses", d, 0, operationId || window.crypto?.randomUUID?.() || ("op-" + Date.now() + "-" + Math.random().toString(36).slice(2)));
export const updateExpense = (id, d) => req("PUT", `/api/expenses/${id}`, d);
export const approveExpense = (id, a) => req("PUT", `/api/expenses/${id}/approve`, { action: a });
export const exportExpenses = () => csvExport("/api/expenses/export/csv");

// ============ AMBASSADORS ============
export const getAmbassadorList = () => req("GET", "/api/expenses/ambassadors");
export const getExpenseAmbassadors = () => req("GET", "/api/expenses/ambassadors");
export const getAmbassadors = () => req("GET", "/api/ambassadors-list");
export const getAmbassadorsList = () => req("GET", "/api/ambassadors-list");
export const getAmbassadorDashboard = () => req("GET", "/api/ambassador/dashboard");
export const submitWithdrawal = (d) => req("POST", "/api/ambassador/withdraw", d);
export const requestWithdrawal = (d) => req("POST", "/api/ambassador/withdraw", d);
export const getWithdrawals = () => req("GET", "/api/ambassador/withdrawals");
export const processWithdrawal = (id, a) => req("PUT", `/api/ambassador/withdrawals/${id}`, { action: a });
export const getAmbassadorMonitor = () => req("GET", "/api/ambassador/monitor");
export const updateCommissionRate = (em, r) => req("PUT", `/api/ambassador/commission/${em}`, { commissionRate: r });
export const updateCommission = (em, r) => req("PUT", `/api/ambassador/commission/${em}`, { commissionRate: r });

// ============ INVENTORY ============
export const getInventory = (p) => req("GET", `/api/inventory?${new URLSearchParams(p)}`);
export const createInventory = (d, operationId) => req("POST", "/api/inventory", d, 0, operationId || window.crypto?.randomUUID?.() || ("op-" + Date.now() + "-" + Math.random().toString(36).slice(2)));
export const bulkUploadRawInventory = (formData) => {
  const token = getToken();
  return fetch(`${BASE}/api/inventory/bulk`, { method: "POST", headers: { "x-auth-token": token }, body: formData })
    .then(async res => { const data = await res.json(); if (!res.ok) throw new Error(data.error || "Upload failed"); return data; });
};
export const downloadRawInventoryTemplate = () => {
  const token = getToken();
  window.open(`${BASE}/api/inventory/template?token=${token}&fresh=${Date.now()}`, "_blank");
};
export const updateInventory = (id, d) => req("PUT", `/api/inventory/${id}`, d);
export const approveInventory = (id, a) => req("PUT", `/api/inventory/${id}/approve`, { action: a });
export const exportInventory = () => csvExport("/api/inventory/export/csv");
// ── Raw inventory ITEM catalog (Tier 13 — real per-item tracking,
// replacing "restock log with no item identity") ──────────────────
export const getRawInventoryItems = (p) => req("GET", `/api/inventory/items?${new URLSearchParams(p || {})}`);
export const getLowStockRawItems = () => req("GET", "/api/inventory/items/low-stock");
export const getRawInventoryItemHistory = (itemId) => req("GET", `/api/inventory/items/${itemId}/history`);
export const createRawInventoryItem = (d) => req("POST", "/api/inventory/items", d);
export const updateRawInventoryItem = (itemId, d) => req("PUT", `/api/inventory/items/${itemId}`, d);
export const adjustRawInventoryItem = (itemId, d) => req("PUT", `/api/inventory/items/${itemId}/adjust`, d);
export const removeRawInventoryItem = (itemId) => req("PUT", `/api/inventory/items/${itemId}/remove`);
export const restoreRawInventoryItem = (itemId) => req("PUT", `/api/inventory/items/${itemId}/restore`);

// ============ CAPITAL & FINANCE ============
export const getCapital = () => req("GET", "/api/capital");
export const createCapital = (d, operationId) => req("POST", "/api/capital", d, 0, operationId || window.crypto?.randomUUID?.() || ("op-" + Date.now() + "-" + Math.random().toString(36).slice(2)));
export const updateCapital = (id, d) => req("PUT", `/api/capital/${id}`, d);
export const getRepayments = () => req("GET", "/api/repayments");
export const getInternalTransfers = () => req("GET", "/api/internal-transfers");
export const createInternalTransfer = (d) => req("POST", "/api/internal-transfers", d);
export const createRepayment = (d, operationId) => req("POST", "/api/repayments", d, 0, operationId || window.crypto?.randomUUID?.() || ("op-" + Date.now() + "-" + Math.random().toString(36).slice(2)));
export const updateRepayment = (id, d) => req("PUT", `/api/repayments/${id}`, d);
export const getAllocations = () => req("GET", "/api/allocations");
export const getAvailableProfit = () => req("GET", "/api/allocations/available-profit");
export const createAllocation = (d) => req("POST", "/api/allocations", d);
export const updateAllocation = (id, d) => req("PUT", `/api/allocations/${id}`, d);
export const getAssets = () => req("GET", "/api/assets");
export const createAsset = (d) => req("POST", "/api/assets", d);

// ============ SALES & PRODUCTS ============
export const getFinishedGoods = (p) => req("GET", `/api/finishedgoods?${new URLSearchParams(p || {})}`);
export const repairFinishedGoodsStockPurchaseExpenses = () => req("POST", "/api/finishedgoods/maintenance/reclassify-stock-purchase-expenses", {});
export const getDemandForecast = (p) => req("GET", `/api/forecasting/products?${new URLSearchParams(p || {})}`);
export const getFinishedGood = (id) => req("GET", `/api/finishedgoods/${id}`);
export const createFinishedGood = (d, operationId) => req("POST", "/api/finishedgoods", d, 0, operationId || window.crypto?.randomUUID?.() || ("op-" + Date.now() + "-" + Math.random().toString(36).slice(2)));
export const updateFinishedGood = (id, d) => req("PUT", `/api/finishedgoods/${id}`, d);
export const deleteFinishedGood = (id) => req("DELETE", `/api/finishedgoods/${id}`);
export const restoreFinishedGood = (id) => req("PUT", `/api/finishedgoods/${id}/restore`);
export const getSales = (p) => req("GET", `/api/sales?${new URLSearchParams(p)}`);
export const getPendingSales = () => req("GET", "/api/sales/pending");
export const getTodaySales = () => req("GET", "/api/sales/today");
export const createSale = (d, operationId) => req("POST", "/api/sales", d, 0, operationId || window.crypto?.randomUUID?.() || ("op-" + Date.now() + "-" + Math.random().toString(36).slice(2)));
export const processSale = (id) => req("PUT", `/api/sales/${id}/process`);
export const cancelSale = (id) => req("PUT", `/api/sales/${id}/cancel`);
// Refund (Tier 17) — parity with order refunds, see routes/sales.js's
// own comment for why a sale's refund restocks by default (pass
// restock:false to skip, e.g. for damaged/unsellable goods).
// Proportional refund (Tier 26) — returnedItems: [{productId, sourceType, quantity}], optional
export const refundSale = (id, amount, reason, returnedItems, operationId) => req("PUT", `/api/sales/${id}/refund`, { amount, reason, returnedItems }, 0, operationId || window.crypto?.randomUUID?.() || ("op-" + Date.now() + "-" + Math.random().toString(36).slice(2)));
export const exportSales = () => csvExport("/api/sales/export/csv");
export const getSaleWhatsappLinks = (id) => req("GET", `/api/sales/${id}/whatsapp-links`);

// ============ DASHBOARD & ANALYTICS ============
export const getDashboard = () => req("GET", "/api/dashboard");
export const getLiquidity = () => req("GET", "/api/dashboard/liquidity");
export const getNotifications = () => req("GET", "/api/dashboard/notifications");
export const getTrends = (from, to) => req("GET", `/api/trends?from=${from}&to=${to}`);
export const getActivityTimeline = (limit = 50) => req("GET", `/api/activity-timeline?limit=${limit}`);
export const getClientDetails = (clientId) => req("GET", `/api/clients/${clientId}`);
export const getTopClients = (limit = 10) => req("GET", `/api/clients/top?limit=${limit}`);

// ============ HISTORY & REPORTS ============
export const getPeriodReport = (f, t) => req("GET", `/api/history?from=${f}&to=${t}`);
export const getAuditLog = (p) => req("GET", `/api/audit?${new URLSearchParams(p)}`);
// Distinct users/modules actually present in this business's audit log
// — populates the filter dropdowns with real, selectable options.
export const getAuditLogFilters = () => req("GET", "/api/audit/filters");

// ============ LEDGER ============
// Client-facing view onto the same Ledger that powers the dashboard's
// moneyIn/moneyOut/liquidCash/combinedNetProfit — see routes/ledger.js.
export const getLedger = (p) => req("GET", `/api/ledger?${new URLSearchParams(p)}`);
export const getLedgerFilters = () => req("GET", "/api/ledger/filters");
export const getLedgerSummary = () => req("GET", "/api/ledger/summary");

// ============ USERS ============
export const getUsers = () => req("GET", "/api/users");
export const createUser = (d) => req("POST", "/api/users", d);
export const updateUser = (em, d) => req("PUT", `/api/users/${em}`, d);
export const deleteUser = (em) => req("DELETE", `/api/users/${em}`);

// ============ LEADERBOARD ============
export const getLeaderboard = () => req("GET", "/api/leaderboard");
export const getSnapshots = () => req("GET", "/api/leaderboard/snapshots");
export const resetLeaderboard = (body) => req("POST", "/api/leaderboard/reset", body);

// ============ AI FEATURES ============
export const aiAnalyse = (d) => req("POST", "/api/ai/analyse-business", d);
export const aiDailyBrief = (d) => req("POST", "/api/ai/daily-brief", d);
export const aiChat = (d) => req("POST", "/api/ai/chat", d);

// ============ ADMIN ============
export const adminOverview = () => req("GET", "/api/admin/overview");
export const adminSetPlan = (id, plan) => req("PUT", `/api/admin/business/${id}/plan`, { plan });
export const adminDeleteBusiness = (id, confirmName) => req("DELETE", `/api/admin/business/${id}`, { confirmName });
export const adminRetryFederationPurge = (id) => req("POST", `/api/admin/business/${encodeURIComponent(id)}/retry-federation-purge`, {});

// ============ ADMIN ANALYTICS (NEW) ============
export const adminGetAllBusinesses = () => req("GET", "/api/admin/businesses/all");
// Granular admin accounts (Tier 12) — superadmin-only management
export const adminListAdmins = () => req("GET", "/api/admin/admins");
export const adminCreateAdmin = (payload) => req("POST", "/api/admin/admins", payload);
export const adminCreateAdminSetupLink = (id) => req("POST", `/api/admin/admins/${id}/setup-link`, {});
export const adminUpdateAdmin = (id, payload) => req("PUT", `/api/admin/admins/${id}`, payload);
export const adminDeleteAdmin = (id) => req("DELETE", `/api/admin/admins/${id}`);
// System health / service usage monitoring
export const adminGetSystemHealth = (refresh) => req("GET", `/api/admin/system-health${refresh ? "?refresh=true" : ""}`);
// Central Admin Audit Log — server.js's GET /api/admin/audit-log.
// filters is a plain object of any of {adminEmail, action, success, page, limit}.
export const adminGetAuditLog = (filters = {}) => req("GET", `/api/admin/audit-log?${new URLSearchParams(filters)}`);
// Non-blocking signup risk queue — see server.js POST /auth/register's
// flagReasons computation. These businesses already have full access;
// this is purely a review list.
export const adminGetFlaggedBusinesses = () => req("GET", "/api/admin/businesses/flagged");
export const adminClearBusinessFlag = (businessId) => req("PUT", `/api/admin/businesses/${businessId}/clear-flag`);
export const adminGetBusinessDetail = (id) => req("GET", `/api/admin/business/${id}/detail`);
export const adminGetRevenueTrends = (params) => req("GET", `/api/admin/analytics/revenue-trends?${new URLSearchParams(params)}`);
export const adminGetGrowthMetrics = (params) => req("GET", `/api/admin/analytics/growth?${new URLSearchParams(params)}`);

// ============ UTILITIES ============
// Data deletion (Tier 19) — replaces the old instant resetAllData().
// getDataBackup fetches the full JSON dump; downloadDataBackup wraps it
// as a real file download (Blob + anchor), since this needs the actual
// JSON body rather than the csvExport() window.open+query-token pattern
// used elsewhere in this file for CSV exports.
export const getDataDeletionStatus = () => req("GET", "/api/business/data-deletion-status");
export const requestDataDeletion = () => req("POST", "/api/business/request-data-deletion");
export const getDataBackup = () => req("GET", "/api/business/data-backup");
export const downloadDataBackup = async (businessName) => {
  const data = await getDataBackup();
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safeName = (businessName || "business").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
  a.href = url;
  a.download = `${safeName}-backup-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const getUserPrefs = () => req("GET", "/api/user/prefs");
export const updateUserPrefs = (d) => req("PUT", "/api/user/prefs", d);

// ============ AMBASSADOR ORDER REQUESTS ============
export const submitAmbassadorOrder = (d) => req("POST", "/api/ambassador/submit-order", d);
export const getPendingAmbassadorOrders = () => req("GET", "/api/ambassador/pending-orders");
export const clearAmbassadorNotifications = () => req("POST", "/api/dashboard/notifications/clear");

// ============ ERP SUBSCRIPTIONS & MARKETPLACE PUBLISHING ============
export const adminGetSubscriptionOverview = () => req("GET", "/api/admin/subscriptions/overview");
export const adminGetUpgradeRequests = (status) => req("GET", `/api/admin/upgrade-requests?status=${status || "pending"}`);
export const adminApproveUpgradeRequest = (id) => req("PUT", `/api/admin/upgrade-requests/${id}/approve`);
export const adminRejectUpgradeRequest = (id, reason) => req("PUT", `/api/admin/upgrade-requests/${id}/reject`, { reason });
// Data deletion review (superadmin view) — the only path that actually
// calls purgeBusinessData. approve requires the explicit
// confirmedWithRequester attestation — see routes/business.js /
// server.js for why.
export const adminGetDataDeletionRequests = (status) => req("GET", `/api/admin/data-deletion-requests?status=${status || "pending"}`);
export const adminApproveDataDeletionRequest = (id, confirmedWithRequester) => req("POST", `/api/admin/data-deletion-requests/${id}/approve`, { confirmedWithRequester });
export const adminRejectDataDeletionRequest = (id, reason) => req("POST", `/api/admin/data-deletion-requests/${id}/reject`, { reason });
// Email Outbox (superadmin view) — every drafted email queued by
// utils/emailOutbox.js, copied and sent manually. Same underlying data
// as AdminAccountPortal.js's granular version (routes/adminOutbox.js).
export const adminGetOutbox = (status) => req("GET", `/api/admin/outbox?status=${status || "pending"}`);
export const adminMarkOutboxSent = (id) => req("PUT", `/api/admin/outbox/${id}/mark-sent`);
export const adminDismissOutboxEntry = (id) => req("PUT", `/api/admin/outbox/${id}/dismiss`);
export const updateBusinessSettings = (data) => req("PUT", "/api/business/settings", data);
export const getBusinessSettings = () => req("GET", "/api/business/settings");
// Plan limits must always reflect the currently running subscription rules,
// never an older browser/proxy response after a plan configuration update.
export const getPlan = () => req("GET", `/api/business/plan?fresh=${Date.now()}`);
// Manual upgrade flow — no payment gateway. A business is shown the
// platform's bank details, pays by transfer outside the app, then
// submits "I've paid" (postUpgradeRequest), which creates a request a
// superadmin reviews from the Admin Portal. Nothing about the
// business's plan changes until that approval happens.
export const createBachsCheckout = (requestedTier, billingCycle) => req("POST", "/api/business/upgrade/checkout", { requestedTier, billingCycle });
export const getUpgradeBankDetails = (tier, cycle) => req("GET", `/api/business/upgrade/bank-details?tier=${tier}&cycle=${cycle}`);
export const getPendingUpgradeRequest = () => req("GET", "/api/business/upgrade/pending");
export const getUpgradeRequestHistory = () => req("GET", "/api/business/upgrade/history");
export const postUpgradeRequest = (requestedTier, billingCycle, paymentNote) =>
  req("POST", "/api/business/upgrade/request", { requestedTier, billingCycle, paymentNote });
export const adminUpdateBusinessSubscription = (id, data) => req("PUT", `/api/admin/business/${id}/subscription`, data);
// Per-product marketplace listing toggle (Tier 20) — mirrors TailorOS's
// one-listing-at-a-time control instead of the old all-or-nothing
// sync-all. See routes/finishedgoods.js's PUT /:id/marketplace-listing.
export const setProductMarketplaceListing = (productId, publicListing) =>
  req("PUT", `/api/finishedgoods/${productId}/marketplace-listing`, { publicListing });
export const reactivateBusiness = (id) => req("PUT", `/api/admin/business/${id}/reactivate`);

// ============ BULK UPLOAD ============
export const bulkUploadProducts = (formData) => {
  const token = getToken();
  return fetch(`${BASE}/api/finishedgoods/bulk`, {
    method: "POST",
    headers: { "x-auth-token": token },
    body: formData
  }).then(async res => {
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Upload failed");
    return data;
  });
};
export const bulkRestockProducts = (formData) => {
  const token = getToken();
  return fetch(`${BASE}/api/finishedgoods/bulk-restock`, {
    method: "POST",
    headers: { "x-auth-token": token },
    body: formData
  }).then(async res => {
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Bulk restock failed");
    return data;
  });
};

// Download templates
export const downloadProductTemplate = () => {
  const token = getToken();
  window.open(`${BASE}/api/finishedgoods/template?token=${token}`, "_blank");
};
export const downloadProductRestockTemplate = () => {
  const token = getToken();
  window.open(`${BASE}/api/finishedgoods/restock-template?token=${token}&fresh=${Date.now()}`, "_blank");
};
// ============ GRANULAR ADMIN ACCOUNTS (Tier 12) ============
// A fourth, still-separate identity/token — see middleware/adminJwtAuth.js.
// Not to be confused with the existing /admin superadmin path, which is
// just an ERP staff (erp_token) account whose email is in the
// ADMIN_EMAIL/isPlatformAdmin list — this is for admin team members who
// have neither an ERP staff Google account nor superadmin status, only a
// specific, limited permission set (routes/adminAccounts.js).
export const saveAdminAccountToken = (t) => localStorage.setItem("admin_account_token", t);
export const getAdminAccountToken = () => localStorage.getItem("admin_account_token");
export const clearAdminAccountToken = () => localStorage.removeItem("admin_account_token");

const adminAcctReq = (method, path, body) =>
  fetch(`${BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(getAdminAccountToken() ? { Authorization: `Bearer ${getAdminAccountToken()}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  }).then(r => r.json().then(d => {
    if (!r.ok) {
      const error = new Error(d.error || "Request failed");
      error.status = r.status;
      throw error;
    }
    return d;
  }));

export const adminAccountSetup = (setupToken, payload) => adminAcctReq("POST", `/api/admin-auth/setup/${setupToken}`, payload);
export const adminAccountLogin = (email, password) => adminAcctReq("POST", "/api/admin-auth/login", { email, password });
export const adminAccountGoogleLogin = (credential) => adminAcctReq("POST", "/api/admin-auth/google", { credential });
export const getAdminAccountMe = () => adminAcctReq("GET", "/api/admin-auth/me");
export const getAdminPortalAuditLog = () => adminAcctReq("GET", "/api/admin-portal/audit-log");

// Data deletion review (granular-admin view) — gated server-side by
// requirePermission('data_deletion'), see routes/adminDataDeletion.js.
// Same underlying DataDeletionRequest documents and purgeBusinessData
// call as the superadmin path above.
export const getAdminDataDeletionRequests = (status) => adminAcctReq("GET", `/api/admin-portal/data-deletion${status && status !== "all" ? `?status=${status}` : ""}`);
export const approveAdminDataDeletionRequest = (id, confirmedWithRequester) => adminAcctReq("POST", `/api/admin-portal/data-deletion/${id}/approve`, { confirmedWithRequester });
export const rejectAdminDataDeletionRequest = (id, reason) => adminAcctReq("POST", `/api/admin-portal/data-deletion/${id}/reject`, { reason });

